import { KmsTypeComponent } from "@kaltura/mediaspace-shared-types";
import styled from "@emotion/styled";
import { Box } from "@kaltura/mediaspace-shared-styled";
import { ActionsMenu, ActionsMenuProps, ErrorBoundary } from "@kaltura/mediaspace-shared-ui";
import { getComponentFromPath, translate } from "@kaltura/mediaspace-shared-utils";
import { useSideBySide } from "../../../right-panel-layout/SideBySideContext";
import { MediaPageComponentExtraProps } from "../../../MediaPageComponentExtraProps";
import { ButtonAnalyticsProvider } from "@kaltura/mediaspace-shared-hooks";
import { IconButton } from "@kaltura/ds-react-components";
import { SideBySide24Icon } from "@kaltura/ds-react-icons";

export interface ActionButtonsProps extends MediaPageComponentExtraProps {
    buttons: KmsTypeComponent[];
    menu: ActionsMenuProps;
}

/**
 * Media action buttons below the player (or other media hero component), like "likes" and "comments".
 */
export const ActionButtons = ({ buttons, menu, onRequestEntryReload }: ActionButtonsProps) => {
    const className = "kms-ds-media-page-title-actions";
    const sideBySide = useSideBySide();

    return (
        <StyledContainer className={className}>
            {buttons.map(({ name, component, props }) => {
                const Component = getComponentFromPath(component);
                return (
                    <ErrorBoundary key={name} fallback={<span />}>
                        <span className={className + "-button"}>
                            <Component
                                {...props}
                                className={props.className + " kms-ds-media-page-title-button"}
                                onRequestEntryReload={onRequestEntryReload}
                            />
                        </span>
                    </ErrorBoundary>
                );
            })}

            <span className={className + "-menu-button"}>
                <ButtonAnalyticsProvider location={"User actions menu on media"}>
                    <ActionsMenu {...menu} />
                </ButtonAnalyticsProvider>
            </span>
            {sideBySide.isSideBySideAllowed && (
                <IconButton
                    className={className + "-side-by-side-button"}
                    onClick={sideBySide.toggleSideBySide}
                    variant={"borderless"}
                    size={"medium"}
                    color={"secondary"}
                    disabled={sideBySide.isSideBySideButtonDisabled}
                    aria-label={
                        sideBySide.isSideBySideEnabled ? translate("Vertical view on") : translate("Vertical view off")
                    }
                >
                    <StyledSideBySideIcon isSideBySideEnabled={sideBySide.isSideBySideEnabled} />
                </IconButton>
            )}
        </StyledContainer>
    );
};

const StyledContainer = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "row",
    gap: theme.spacing(1),
}));

const StyledSideBySideIcon = styled(
    SideBySide24Icon,
    {
        shouldForwardProp: (prop) => prop !== "isSideBySideEnabled",
    }
)<{ isSideBySideEnabled: boolean }>(({ isSideBySideEnabled }) => ({
    transform: isSideBySideEnabled ? "rotate(90deg)" : "none",
}));
